/**
 * How much a festival is worth promoting to the top of the Events page.
 *
 * Not every event classed `is_festival` is the kind of thing Prii is for. "And Always
 * Forever - 2 DAY PASS" and "Fuego Fest" are both at the Echoplex: a club night with a
 * long bill, a few hundred people, one room. Nobody loses their friends there and a map
 * of it would be a rectangle. Coachella, Outside Lands, Portola and CRSSD are the case
 * the product exists for (operator, 2026-09-22: "if it doesn't really get 5-10k people
 * we don't care about it as much — the benefit of a map isn't as big for those").
 *
 * The honest caveat: the column that would answer this properly,
 * `venues.venue_capacity_estimate`, is NULL on all 238 venues. Until it is backfilled,
 * the venue's TIER is the only size signal in the data, and it is coarse — Glen Helen
 * (Nocturnal Wonderland, a genuinely large festival) sits at 'other' next to much
 * smaller grounds. Tier gets the Echoplex case right, which is the one that was wrong.
 *
 * Deliberately NOT used: the lineup's artist count. It measures how completely WE
 * ingested a lineup, not how big the festival is — Nocturnal Wonderland has 16 rows and
 * 30,000 people, so ranking by it would push real festivals below small ones that
 * happen to be well scraped.
 */

/** Tier -> weight. 0 means "do not promote": the venue is a room, not grounds. */
const TIER_WEIGHT = {
  festival: 3,
  stadium: 2,
  arena: 2,
  other: 1,
  theatre: 0,
  club: 0,
  intimate: 0,
  bar: 0,
};

export function venueTierOf(event) {
  const venues = Array.isArray(event?.venues) ? event.venues[0] : event?.venues;
  return String(venues?.venue_tier || event?.venue_tier || 'other').toLowerCase();
}

/** 0 = leave it in the ordinary feed; higher = closer to the top. */
export function festivalPriority(event) {
  return TIER_WEIGHT[venueTierOf(event)] ?? 1;
}

/** True when this festival is big enough to lead the page. */
export function isPromotableFestival(event) {
  return festivalPriority(event) > 0;
}

function startMs(event) {
  const raw = event?.event_datetime || event?.event_date;
  const ms = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
}

const goingOf = (e) =>
  typeof e?.going_count === 'number' ? e.going_count : Number(e?.going_count) || 0;
const recurringOf = (e) => (e?.is_recurring ? 1 : 0);

/**
 * The promotable festivals in the order they should appear, which is the page's order
 * applied among themselves: RSVP popularity first, then size, then soonest.
 *
 * Popularity leads even here — Portola with 4 going sits above Ocean Way with 3, though
 * both are festival grounds on the same date. Size leads date, because a Coachella six
 * months out is more interesting than a small one this weekend; that is the whole point
 * of promoting them at all.
 */
export function rankFestivals(events = []) {
  return events
    .filter(isPromotableFestival)
    .slice()
    .sort(
      (a, b) =>
        goingOf(b) - goingOf(a) ||
        festivalPriority(b) - festivalPriority(a) ||
        startMs(a) - startMs(b),
    );
}

/**
 * Slide the festivals into the feed without disturbing it.
 *
 * The order the operator asked for (2026-09-22) is the feed's own, with one key added:
 *   1. RSVP popularity   — an event lots of people are going to still wins
 *   2. festivals         — then a festival beats a concert with the same count
 *   3. the old tiering   — and below that nothing changes (get_popular_events orders on
 *                          is_recurring, going_count, genre bucket, demo_priority, date,
 *                          venue tier; the rows arrive in that order and keep it)
 *
 * So this is a STABLE MERGE on (is_recurring, going_count, festival-first), not a re-sort:
 * feed rows keep their relative positions exactly, and only the festivals move. It is safe
 * across pagination because the server already sorts by going_count DESC — later pages
 * carry counts no higher than earlier ones, so appending a page never reorders what is
 * already on screen.
 *
 * Some festivals land below the hero carousel as a result, which is the point: the
 * carousel is the popular events, and a festival nobody has RSVP'd to does not outrank them.
 */
export function mergeFestivalsIntoFeed(feed = [], festivals = []) {
  const ranked = rankFestivals(festivals);
  if (ranked.length === 0) return feed;

  const beats = (fest, row) => {
    if (recurringOf(fest) !== recurringOf(row)) return recurringOf(fest) < recurringOf(row);
    if (goingOf(fest) !== goingOf(row)) return goingOf(fest) > goingOf(row);
    return !row.is_festival; // same popularity: the festival goes first
  };

  const out = [];
  let i = 0;
  for (const row of feed) {
    while (i < ranked.length && beats(ranked[i], row)) out.push(ranked[i++]);
    out.push(row);
  }
  while (i < ranked.length) out.push(ranked[i++]);
  return out;
}
